import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { AdminSignedAgreement } from '../../types';

interface SignedAgreementsCardProps {
  garageId: string;
}

type Toast = { message: string; type: 'success' | 'error' };

/** The screen that grants the download (identity V24; SUPER_ADMIN always has it). */
export const SIGNED_AGREEMENTS_SCREEN = 'signed-agreements';

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  AUTHORISED_SIGNATORY: 'Authorised signatory',
};

const ORIGIN_CHIP: Record<string, { label: string; cls: string }> = {
  SIGNED: { label: 'Signed', cls: 'signed-agreement-origin-signed' },
  RECONSTRUCTED: { label: 'Reconstructed', cls: 'warning' },
  UNVERIFIED: { label: 'Unverified', cls: 'warning' },
  // Spec 074 — the terms update email was delivered: evidence of notice, never a signature (v2 label).
  NOTIFIED: { label: 'Email notice', cls: 'terms-notified' },
};

const PDF_UNAVAILABLE: Record<string, string> = {
  PENDING: 'PDF pending',
  FAILED: 'PDF failed',
  NOT_REQUIRED: 'No PDF',
};

/** UAE time, as Flow B asks ("Agreed at (UAE time)"). */
const formatUae = (iso: string): string => {
  try {
    return `${new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })} GST`;
  } catch {
    return iso;
  }
};

/**
 * Spec 071 Flow B / C — replaces the old "Terms Agreement Status" card. Every acceptance record
 * of the garage, newest first, with origin (Signed / Reconstructed / Unverified / Notified) and
 * Current / Superseded. Download is shown only to holders of the `signed-agreements` screen and
 * only when the PDF is stored; the server re-checks the permission and audits the download.
 */
export default function SignedAgreementsCard({ garageId }: SignedAgreementsCardProps) {
  const auth = useAuth();
  const canDownload = auth.hasScreen(SIGNED_AGREEMENTS_SCREEN);
  const [rows, setRows] = useState<AdminSignedAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = (message: string, type: Toast['type']) => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api.getGarageSignedAgreements(garageId, auth.token)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load signed agreements'))
      .finally(() => setLoading(false));
  }, [garageId, auth.token]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (row: AdminSignedAgreement) => {
    if (!auth.token) return;
    setDownloadingId(row.id);
    try {
      const { downloadUrl } = await api.getSignedAgreementDownloadUrl(row.id, auth.token);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast('PDF is still being prepared', 'error');
      } else if (err instanceof ApiError && err.status === 403) {
        showToast("You don't have permission to download signed agreements.", 'error');
      } else {
        showToast(err instanceof Error ? err.message : 'Download failed', 'error');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="admin-card" data-testid="signed-agreements-card">
      <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Signed agreements</h3>

      {loading ? (
        <p className="signed-agreements-muted">Loading signed agreements...</p>
      ) : error ? (
        <div className="admin-info-banner danger signed-agreements-error" role="alert">
          <span>{error}</span>
          <button type="button" className="admin-btn admin-btn-outline signed-agreements-btn" onClick={load}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="admin-empty-state" style={{ padding: '1.5rem 1rem' }}>
          <p>No recorded acceptance for this garage.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table signed-agreements-table">
            <thead>
              <tr>
                <th scope="col">Document</th>
                <th scope="col">Version</th>
                <th scope="col">Signer</th>
                <th scope="col">Agreed at (UAE)</th>
                <th scope="col">IP</th>
                <th scope="col">Origin</th>
                <th scope="col">Status</th>
                {canDownload && <th scope="col"><span className="signed-agreements-sr-only">PDF</span></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const origin = ORIGIN_CHIP[row.origin] ?? { label: row.origin, cls: 'warning' };
                const current = row.status === 'CURRENT';
                const stored = row.pdfStatus === 'STORED';
                return (
                  <tr key={row.id} data-testid="signed-agreement-row">
                    <td style={{ fontWeight: 500 }}>{row.documentTitle}</td>
                    <td>v{row.version}</td>
                    <td>
                      {row.signerName ? (
                        <>
                          <span dir="auto">{row.signerName}</span>
                          {row.signerRole && (
                            <span className="signed-agreements-muted-inline">
                              {' · '}{ROLE_LABEL[row.signerRole] ?? row.signerRole}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="signed-agreements-muted-inline">Not recorded</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatUae(row.agreedAt)}</td>
                    <td className="signed-agreements-ip">{row.ipAddress || '—'}</td>
                    <td><span className={`admin-badge ${origin.cls}`}>{origin.label}</span></td>
                    <td>
                      <span className={`admin-badge ${current ? 'approved' : 'signed-agreement-superseded'}`}>
                        {current ? 'Current' : 'Superseded'}
                      </span>
                    </td>
                    {canDownload && (
                      <td>
                        {stored ? (
                          <button
                            type="button"
                            className="admin-btn admin-btn-outline signed-agreements-btn"
                            onClick={() => void handleDownload(row)}
                            disabled={downloadingId === row.id}
                            aria-label={`Download ${row.documentTitle} v${row.version}`}
                            title="Download the signed PDF"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {downloadingId === row.id ? 'Preparing…' : 'Download'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="admin-btn admin-btn-outline signed-agreements-btn"
                            disabled
                          >
                            {PDF_UNAVAILABLE[row.pdfStatus] ?? 'PDF pending'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div className={`admin-toast ${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
}
